/**
 * Magazzino — **cosa c'è, di chi è, da quanto è lì.**
 *
 * ## Cos'era, e perché è cambiato (30-08-2026)
 *
 * Fino a ieri questa sezione si chiamava Magazzino ma non lo era: leggeva
 * `articoli-critici` e `stock-eccedente`, cioè la giacenza **vista dal lato della
 * domanda** — cosa manca per le commesse, cosa avanza rispetto agli ordini. Il
 * magazzino come cosa a sé — i pallet, i lotti, le ubicazioni, le scadenze, da
 * quanto tempo una unità non si muove — non compariva da nessuna parte: entrava
 * nel motore di evadibilità, veniva aggregato per articolo, e spariva.
 *
 * Enrico, aprendo il giro: *«nel database sono presenti sia gli articoli (con le
 * giacenze e le produzioni previste) che i clienti, ma non abbiamo nessun modulo
 * di consultazione per loro»*.
 *
 * ## Le tre scelte che tengono in piedi la pagina
 *
 * **1. Una tabella sola, di articoli, con le due letture affiancate.** A sinistra
 * l'offerta (quanti pallet, quanti impegnati, da quanto fermi), a destra la
 * domanda scoperta (quanto manca). Erano due liste diverse e potevano
 * contraddirsi — è successo il 19-08, quando il 39% degli articoli «che mancano»
 * non mancava di niente.
 *
 * **2. Il grande numero di un riquadro è sempre «quante righe vedrai».** Il
 * piccolo è quanto magazzino pesa. Mescolare un conteggio di articoli e uno di
 * pallet nella stessa fila di riquadri è l'errore per cui la prima versione di
 * questa pagina era illeggibile (*«mi dà la sensazione che sia meno efficace,
 * anche se non saprei dirti perché»*).
 *
 * **3. La ricerca scavalca il filtro** (richiesta di Enrico: *«non solo quelli
 * che non sono previsti in produzione ma tutti gli articoli»*). Un articolo che
 * non compare perché il filtro attivo lo escludeva è indistinguibile, a schermo,
 * da un articolo che non esiste.
 *
 * La grana fine — il singolo pallet, con lotto, ubicazione e **a chi è
 * destinato** — sta nel drawer, non in tabella: su 3.713 unità sarebbe rumore.
 */
import { Ban, PackageX, RotateCcw, Search, Warehouse } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ArticoloDrawer } from "@/components/m1/ArticoloDrawer";
import { PulsanteStampaScadenze } from "@/components/m1/StampaScadenze";
import { Paginazione } from "@/components/Paginazione";
import { RigaTesta } from "@/components/RigaTesta";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useM1ImpostaEsclusione } from "@/hooks/useM1";
import {
  FILTRI_MAGAZZINO,
  type FiltroMagazzino,
  useMagazzino,
} from "@/hooks/useMagazzino";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";
import type { MagArticolo, MagConteggio } from "@/types/api";

const PER_PAGINA = 25;
const fmt = (v: string | number | null | undefined) => num(Number(v ?? 0));

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dataBreve(iso: string | null): string {
  if (!iso) return "—";
  const [, m, g] = iso.split("-").map(Number);
  return `${g} ${MESI[m - 1]}`;
}

/** I riquadri, in quest'ordine e con queste parole. L'ordine non è casuale: si
 *  parte da «cosa c'è», si passa a «di chi è», si finisce con «cosa non c'è». */
const RIQUADRI: {
  key: FiltroMagazzino;
  label: string;
  sotto: (c: MagConteggio, t: { tonnellate: number; scaduti: number; giorniFerma: number }) => string;
  tono?: "alert" | "warn";
}[] = [
  {
    key: "a_magazzino",
    label: "A magazzino",
    sotto: (c, t) => `${fmt(c.pallet)} pallet · ${num(t.tonnellate)} t`,
  },
  {
    key: "non_ritirati",
    label: "Non ritirati",
    sotto: (c) => `${fmt(c.pallet)} pallet · consegna passata`,
    tono: "warn",
  },
  {
    key: "liberi",
    label: "Liberi",
    sotto: (c) => `${fmt(c.pallet)} pallet · nessuna commessa`,
  },
  {
    key: "fermi",
    label: "Fermi",
    sotto: (c, t) => `${fmt(c.pallet)} pallet · oltre ${t.giorniFerma} giorni`,
    tono: "warn",
  },
  {
    key: "fine_vita",
    label: "A fine vita",
    sotto: (c, t) =>
      t.scaduti > 0 ? `${fmt(c.pallet)} pallet · ${t.scaduti} già scaduti` : `${fmt(c.pallet)} pallet`,
    tono: "alert",
  },
  {
    key: "mancano",
    label: "Che mancano",
    sotto: (c) => `${fmt(c.manca)} pezzi di buco`,
    tono: "alert",
  },
];

export function MagazzinoM1() {
  const navigate = useNavigate();
  const impostaEsclusione = useM1ImpostaEsclusione();

  // ⚠️ Filtro **e** ricerca vivono nell'URL. Il filtro perché è la correzione del
  // 16-08 («l'URL è l'unica fonte»); la ricerca perché una lista cercata è un
  // indirizzo che si manda a qualcuno — «guarda questo codice» — e tenerla in
  // `useState` la rendeva incondivisibile.
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroDaUrl = searchParams.get("magazzino") as FiltroMagazzino | null;
  const filtro: FiltroMagazzino = FILTRI_MAGAZZINO.includes(filtroDaUrl as FiltroMagazzino)
    ? (filtroDaUrl as FiltroMagazzino)
    : "a_magazzino";
  const cerca = searchParams.get("mq") ?? "";

  function aggiornaUrl(patch: { filtro?: FiltroMagazzino; cerca?: string }) {
    const p = new URLSearchParams(searchParams);
    if (patch.filtro !== undefined) {
      if (patch.filtro === "a_magazzino") p.delete("magazzino");
      else p.set("magazzino", patch.filtro);
    }
    if (patch.cerca !== undefined) {
      if (patch.cerca.trim()) p.set("mq", patch.cerca);
      else p.delete("mq");
    }
    setSearchParams(p, { replace: true });
  }

  /** La ricerca digitata, non ancora mandata al server. Senza il rimbalzo si
   *  farebbe una richiesta per tasto su una lista di 5.991 articoli. */
  const [bozzaCerca, setBozzaCerca] = useState(cerca);
  useEffect(() => setBozzaCerca(cerca), [cerca]);
  useEffect(() => {
    if (bozzaCerca === cerca) return;
    const t = setTimeout(() => aggiornaUrl({ cerca: bozzaCerca }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozzaCerca]);

  /** La pagina resta in `useState`: il filtro è un indirizzo che si condivide,
   *  la pagina è dove sei arrivato a scorrere. */
  const [pagina, setPagina] = useState(0);
  useEffect(() => setPagina(0), [filtro, cerca]);

  const { data, isLoading, isPlaceholderData } = useMagazzino({
    filtro,
    cerca,
    offset: pagina * PER_PAGINA,
    limit: PER_PAGINA,
  });

  const [articoloAperto, setArticoloAperto] = useState<string | null>(null);
  const [daEscludere, setDaEscludere] = useState<MagArticolo | null>(null);
  const [motivo, setMotivo] = useState("");
  const [appenaEscluso, setAppenaEscluso] = useState<{ codice: string; nome: string } | null>(null);

  const conteggi = data?.conteggi;
  const totali = data?.totali;
  const contesto = useMemo(() => {
    if (!data) return "";
    if (cerca.trim()) return `${fmt(data.total)} articoli per «${cerca.trim()}»`;
    return `${fmt(data.total)} articoli`;
  }, [data, cerca]);

  const apriCommessa = (numero: number) =>
    navigate(`/moduli/order-readiness?tab=portafoglio&q=${encodeURIComponent(String(numero))}`);

  return (
    <div className="space-y-3">
      <RigaTesta
        contesto={contesto}
        caricatoIl={data?.caricato_il ?? undefined}
        destra={ORDINE_DI[filtro]}
      />

      <section className="grid grid-cols-2 gap-2 lg:grid-cols-6">
        {RIQUADRI.map((r) => (
          <RiquadroFiltro
            key={r.key}
            label={r.label}
            valore={conteggi?.[r.key]?.articoli}
            sotto={
              conteggi && totali
                ? r.sotto(conteggi[r.key], {
                    tonnellate: totali.tonnellate,
                    scaduti: totali.pallet_scaduti,
                    giorniFerma: data?.giorni_ferma ?? 90,
                  })
                : "—"
            }
            tono={r.tono}
            // ⚠️ Attivo **solo se non si sta cercando**: durante una ricerca il
            // filtro non governa la lista, e lasciare un riquadro acceso direbbe
            // una bugia su cosa stai guardando.
            attivo={filtro === r.key && !cerca.trim()}
            onClick={() => aggiornaUrl({ filtro: r.key, cerca: "" })}
            loading={isLoading}
          />
        ))}
      </section>

      {appenaEscluso && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-brand-600/40 bg-brand-50/60 px-3 py-2 text-[12.5px] dark:bg-brand-950/40">
          <span>
            <b>{appenaEscluso.nome}</b> non blocca più l&apos;evadibilità delle commesse.
          </span>
          <button
            type="button"
            onClick={() => aggiornaUrl({ filtro: "esclusi", cerca: "" })}
            className="font-medium text-brand-700 underline-offset-2 hover:underline dark:text-brand-400"
          >
            Lo trovi in «Esclusi»
          </button>
          <button
            type="button"
            disabled={impostaEsclusione.isPending}
            onClick={() =>
              impostaEsclusione.mutate(
                { codice: appenaEscluso.codice, escluso_da_evadibilita: false },
                { onSuccess: () => setAppenaEscluso(null) }
              )
            }
            className="ml-auto inline-flex items-center gap-1 rounded border bg-card px-2 py-1 text-[11px] hover:bg-accent disabled:opacity-50"
          >
            <RotateCcw className="size-3" />
            Annulla
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="relative min-w-[260px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={bozzaCerca}
            onChange={(e) => setBozzaCerca(e.target.value)}
            placeholder={`Cerca fra tutti i ${fmt(conteggi?.tutti?.articoli)} articoli — codice o descrizione…`}
            className="h-9 w-full rounded-md border bg-card pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {/* Due liste che non sono misure del magazzino: stanno accanto alla
            ricerca e non fra i riquadri, dove avrebbero il peso di un dato. */}
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <LinkFiltro
            attivo={filtro === "tutti" && !cerca.trim()}
            onClick={() => aggiornaUrl({ filtro: "tutti", cerca: "" })}
          >
            Tutti gli articoli ({fmt(conteggi?.tutti?.articoli)})
          </LinkFiltro>
          <LinkFiltro
            attivo={filtro === "senza_piano" && !cerca.trim()}
            onClick={() => aggiornaUrl({ filtro: "senza_piano", cerca: "" })}
          >
            Senza piano ({fmt(conteggi?.senza_piano?.articoli)})
          </LinkFiltro>
          <LinkFiltro
            attivo={filtro === "esclusi" && !cerca.trim()}
            onClick={() => aggiornaUrl({ filtro: "esclusi", cerca: "" })}
          >
            Esclusi ({fmt(conteggi?.esclusi?.articoli)})
          </LinkFiltro>
        </div>
        {/* Il foglio dei pallet scaduti e in scadenza (Enzo, 12-10-2026): sta qui
            e non fra i riquadri perché non filtra la tabella, la stampa. */}
        <PulsanteStampaScadenze />
      </div>

      {cerca.trim() && (
        <p className="text-[11px] text-muted-foreground">
          La ricerca guarda <b className="text-foreground">tutti</b> gli articoli, anche quelli
          fuori dal filtro:{" "}
          <button
            type="button"
            onClick={() => aggiornaUrl({ cerca: "" })}
            className="font-medium text-brand-700 underline-offset-2 hover:underline dark:text-brand-400"
          >
            torna a «{RIQUADRI.find((r) => r.key === filtro)?.label ?? "Tutti gli articoli"}»
          </button>
        </p>
      )}

      <div className={cn("rounded-lg border bg-card", isPlaceholderData && "opacity-60")}>
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <TabellaMagazzino
            righe={data?.items ?? []}
            soglia={data?.soglia_fine_vita_pct ?? 25}
            giorniFerma={data?.giorni_ferma ?? 90}
            onApri={setArticoloAperto}
            onEscludi={(a) => {
              setDaEscludere(a);
              setMotivo("");
            }}
            onRipristina={(codice) => {
              impostaEsclusione.mutate({ codice, escluso_da_evadibilita: false });
              setAppenaEscluso(null);
            }}
            esclusi={filtro === "esclusi" && !cerca.trim()}
          />
        )}
        <Paginazione
          pagina={pagina}
          perPagina={PER_PAGINA}
          totale={data?.total ?? 0}
          unita="articoli"
          onVai={setPagina}
          formatta={(n) => fmt(n)}
        />
      </div>

      <NotaInFondo filtro={filtro} data={data} />

      <ArticoloDrawer
        codice={articoloAperto}
        onClose={() => setArticoloAperto(null)}
        onApriCommessa={apriCommessa}
        onEscludi={(codice) => {
          const a = (data?.items ?? []).find((x) => x.codice === codice);
          setArticoloAperto(null);
          setDaEscludere(a ?? ({ codice, descrizione: null } as MagArticolo));
          setMotivo("");
        }}
      />

      <DialogEsclusione
        articolo={daEscludere}
        motivo={motivo}
        setMotivo={setMotivo}
        pending={impostaEsclusione.isPending}
        onClose={() => setDaEscludere(null)}
        onConferma={() => {
          if (!daEscludere) return;
          const nome = daEscludere.descrizione ?? daEscludere.codice;
          impostaEsclusione.mutate(
            {
              codice: daEscludere.codice,
              escluso_da_evadibilita: true,
              motivo_esclusione: motivo.trim() || null,
            },
            {
              onSuccess: () => {
                setAppenaEscluso({ codice: daEscludere.codice, nome });
                setDaEscludere(null);
              },
            }
          );
        }}
      />
    </div>
  );
}

/** Cosa c'è in cima, per ogni filtro. Dirlo evita la domanda «perché questo
 *  articolo è primo?», che nella versione di agosto non aveva risposta. */
const ORDINE_DI: Record<FiltroMagazzino, string> = {
  a_magazzino: "dai più grossi",
  non_ritirati: "da chi ne tiene fermi di più",
  liberi: "da chi ne ha di più senza commessa",
  fermi: "dai più vecchi",
  fine_vita: "da chi ha meno vita davanti",
  mancano: "ordinati per commesse sbloccate",
  senza_piano: "ordinati per commesse sbloccate",
  esclusi: "dai più grossi",
  tutti: "dai più grossi",
};

function NotaInFondo({
  filtro,
  data,
}: {
  filtro: FiltroMagazzino;
  data: ReturnType<typeof useMagazzino>["data"];
}) {
  if (!data) return null;
  const t = data.totali;
  if (filtro === "non_ritirati") {
    return (
      <p className="text-[11px] text-muted-foreground">
        Merce già venduta che il cliente non è venuto a prendere:{" "}
        <b className="text-foreground">{fmt(t.pallet_non_ritirati)} pallet</b> su{" "}
        {fmt(t.pallet)}. La misura è un fatto — la data di consegna è passata — non una
        marcatura: le commesse «a chiamata» si possono marcare dal Portafoglio, e finché
        nessuno lo fa questa è la lettura più onesta.
      </p>
    );
  }
  if (filtro === "fine_vita") {
    return (
      <p className="text-[11px] text-muted-foreground">
        Sotto il <b className="text-foreground">{data.soglia_fine_vita_pct}%</b> di vita
        residua, calcolata su una shelf life di{" "}
        <b className="text-foreground">{data.shelf_life_default} mesi</b>
        {data.n_override_shelf_life > 0 && ` (${data.n_override_shelf_life} articoli ne hanno una propria)`}.
        Si cambia dalle Impostazioni del modulo.
      </p>
    );
  }
  if (filtro === "senza_piano") {
    return (
      <p className="text-[11px] text-muted-foreground">
        Manca <b className="text-foreground">e</b> nessuno lo sta producendo: né la giacenza
        né il piano del MES coprono queste commesse. È il ponte verso la Pianificazione —
        finché un articolo è qui, la data non si sposta da sola.
      </p>
    );
  }
  if (filtro === "esclusi") {
    return (
      <p className="text-[11px] text-muted-foreground">
        Materiali di servizio e documenti: non sono prodotto finito, quindi non bloccano
        l&apos;evadibilità di una commessa. La stessa marcatura è già rispettata dalla
        Pianificazione.
      </p>
    );
  }
  return (
    <p className="text-[11px] text-muted-foreground">
      Fotografia dei due magazzini: automatico BC 700 e{" "}
      <b className="text-foreground">{fmt(t.pallet_a_terra)} pallet</b> a terra (BC 710).
      {t.articoli_senza_anagrafica > 0 && (
        <>
          {" "}
          ⚠️ {t.articoli_senza_anagrafica} codici a stock non esistono in anagrafica articoli:
          si vedono col codice al posto del nome.
        </>
      )}
    </p>
  );
}

function LinkFiltro({
  attivo,
  onClick,
  children,
}: {
  attivo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "underline-offset-2 hover:underline",
        attivo && "font-semibold text-brand-700 dark:text-brand-400"
      )}
    >
      {children}
    </button>
  );
}

// ── La tabella ───────────────────────────────────────────────────────

function TabellaMagazzino({
  righe,
  soglia,
  giorniFerma,
  onApri,
  onEscludi,
  onRipristina,
  esclusi,
}: {
  righe: MagArticolo[];
  soglia: number;
  giorniFerma: number;
  onApri: (codice: string) => void;
  onEscludi: (a: MagArticolo) => void;
  onRipristina: (codice: string) => void;
  esclusi: boolean;
}) {
  if (!righe.length) {
    return <p className="p-6 text-center text-sm text-muted-foreground">Nessun articolo.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead className="bg-muted/40 text-[10.5px] text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Articolo</th>
            <th className="px-3 py-2 text-right font-medium">Pallet</th>
            <th className="px-3 py-2 text-left font-medium">Di chi è</th>
            <th className="px-3 py-2 text-right font-medium">Fermo da</th>
            <th className="px-3 py-2 text-left font-medium">Vita residua</th>
            <th className="px-3 py-2 text-right font-medium">Manca</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {righe.map((a) => (
            <tr
              key={a.codice}
              className="cursor-pointer hover:bg-brand-50/50 dark:hover:bg-brand-950/30"
              onClick={() => onApri(a.codice)}
              title="Apre il dettaglio: i pallet e la coda di allocazione"
            >
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[11.5px] font-semibold">{a.codice}</span>
                  {a.pallet_a_terra > 0 && <Etichetta>a terra {a.pallet_a_terra}</Etichetta>}
                  {a.escluso && <Etichetta tono="muted">escluso</Etichetta>}
                  {a.dismesso && <Etichetta tono="muted">dismesso</Etichetta>}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {a.descrizione ?? "senza descrizione in anagrafica"}
                </div>
              </td>

              <td className="px-3 py-2 text-right tabular-nums">
                {a.pallet > 0 ? (
                  <>
                    <div className="text-[15px] font-semibold leading-tight">{fmt(a.pallet)}</div>
                    <div className="text-[9.5px] text-muted-foreground">{fmt(a.pezzi)} pz</div>
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>

              {/* «Di chi è»: la risposta alla domanda di Enrico, in una cella.
                  La barra dice le proporzioni, i numeri sotto le dicono esatte. */}
              <td className="px-3 py-2">
                {a.pallet > 0 ? (
                  <>
                    <BarraDestinazione a={a} />
                    <div className="mt-0.5 flex flex-wrap gap-x-2 text-[9.5px] text-muted-foreground">
                      {a.pallet_non_ritirati > 0 && (
                        <span className="font-medium text-amber-700 dark:text-amber-400">
                          {a.pallet_non_ritirati} non ritirati
                        </span>
                      )}
                      {a.pallet_impegnati - a.pallet_non_ritirati > 0 && (
                        <span>{a.pallet_impegnati - a.pallet_non_ritirati} impegnati</span>
                      )}
                      {a.pallet_liberi > 0 && <span>{a.pallet_liberi} liberi</span>}
                      {a.pallet_scaduti > 0 && (
                        <span className="font-medium text-red-700 dark:text-red-400">
                          {a.pallet_scaduti} scaduti
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <span className="text-[10px] text-muted-foreground">niente a magazzino</span>
                )}
              </td>

              <td className="px-3 py-2 text-right tabular-nums">
                {a.fermo_da_giorni === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span
                    className={cn(
                      a.fermo_da_giorni > giorniFerma && "font-semibold text-amber-700 dark:text-amber-400"
                    )}
                    title="Giorni dall'ultimo movimento del pallet più vecchio"
                  >
                    {a.fermo_da_giorni} g
                  </span>
                )}
              </td>

              <td className="px-3 py-2">
                <VitaResidua a={a} soglia={soglia} />
              </td>

              <td className="px-3 py-2 text-right tabular-nums">
                {Number(a.manca) > 0 ? (
                  <>
                    <div className="font-semibold text-red-700 dark:text-red-400">
                      {fmt(a.manca)}
                    </div>
                    <div className="text-[9.5px] text-muted-foreground">
                      ferma {a.n_commesse_in_attesa}{" "}
                      {a.n_commesse_in_attesa === 1 ? "commessa" : "commesse"}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>

              <td className="px-2 py-2 text-right">
                {esclusi ? (
                  <button
                    type="button"
                    title="Rimetti l'articolo nel conteggio dell'evadibilità"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRipristina(a.codice);
                    }}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <RotateCcw className="size-3.5" />
                  </button>
                ) : (
                  !a.escluso && (
                    <button
                      type="button"
                      title="Non è prodotto finito: non deve bloccare l'evadibilità"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEscludi(a);
                      }}
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                      <Ban className="size-3.5" />
                    </button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Le proporzioni dei pallet dell'articolo, in una barra alta quattro pixel.
 *  Nell'ordine in cui si legge la riga: quel che aspetta un cliente in ritardo,
 *  quel che è impegnato regolarmente, quel che è libero, quel che è scaduto. */
function BarraDestinazione({ a }: { a: MagArticolo }) {
  const fette = [
    { n: a.pallet_non_ritirati, cl: "bg-amber-500", t: "non ritirati (consegna passata)" },
    { n: a.pallet_impegnati - a.pallet_non_ritirati, cl: "bg-brand-600", t: "impegnati" },
    { n: a.pallet_liberi, cl: "bg-muted-foreground/30", t: "liberi" },
    { n: a.pallet_scaduti, cl: "bg-red-600", t: "scaduti" },
  ].filter((f) => f.n > 0);
  return (
    <div className="flex h-1.5 w-full max-w-[160px] gap-px overflow-hidden rounded-full bg-muted">
      {fette.map((f) => (
        <div
          key={f.t}
          className={f.cl}
          style={{ width: `${(100 * f.n) / Math.max(a.pallet, 1)}%` }}
          title={`${f.n} ${f.t}`}
        />
      ))}
    </div>
  );
}

function VitaResidua({ a, soglia }: { a: MagArticolo; soglia: number }) {
  if (a.pallet === 0) return <span className="text-muted-foreground">—</span>;
  if (a.prima_scadenza === null) {
    return (
      <span className="text-[10px] text-muted-foreground" title="Nessuna data di scadenza sul file">
        non indicata
      </span>
    );
  }
  const pct = a.residua_pct_min;
  const critica = a.pallet_scaduti > 0 || (pct !== null && pct <= soglia);
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 w-10 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full", critica ? "bg-red-600" : "bg-emerald-600")}
          style={{ width: `${Math.max(pct ?? 0, 2)}%` }}
        />
      </div>
      <span
        className={cn("text-[10.5px] tabular-nums", critica && "font-semibold text-red-700 dark:text-red-400")}
        title={`Primo lotto in scadenza il ${a.prima_scadenza} · shelf life ${a.shelf_life_mesi} mesi${a.shelf_life_impostata ? " (impostata su questo articolo)" : ""}`}
      >
        {pct === null ? "—" : `${Math.round(pct)}%`}
        <span className="ml-1 text-muted-foreground">{dataBreve(a.prima_scadenza)}</span>
      </span>
    </div>
  );
}

function Etichetta({
  children,
  tono,
}: {
  children: React.ReactNode;
  tono?: "muted";
}) {
  return (
    <span
      className={cn(
        "rounded-full px-1.5 py-px text-[9px] font-medium",
        tono === "muted"
          ? "bg-muted text-muted-foreground"
          : "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300"
      )}
    >
      {children}
    </span>
  );
}

function RiquadroFiltro({
  label,
  valore,
  sotto,
  tono,
  attivo,
  onClick,
  loading,
}: {
  label: string;
  valore: number | undefined;
  sotto: string;
  tono?: "alert" | "warn";
  attivo: boolean;
  onClick: () => void;
  loading?: boolean;
}) {
  const colore =
    valore === 0
      ? "text-muted-foreground"
      : tono === "alert"
        ? "text-red-700 dark:text-red-400"
        : tono === "warn"
          ? "text-amber-700 dark:text-amber-400"
          : "text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={attivo}
      className={cn(
        "rounded-lg border bg-card p-2.5 text-left transition-colors",
        attivo
          ? "border-brand-600 bg-brand-50/60 ring-1 ring-inset ring-brand-600 dark:bg-brand-950/40"
          : "hover:bg-accent/40"
      )}
    >
      <span className="block text-[11px] text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton className="my-1 h-6 w-10" />
      ) : (
        <span className={cn("block text-2xl font-semibold leading-tight tabular-nums", colore)}>
          {fmt(valore ?? 0)}
        </span>
      )}
      <span className="block text-[10px] text-muted-foreground">{sotto}</span>
    </button>
  );
}

function DialogEsclusione({
  articolo,
  motivo,
  setMotivo,
  pending,
  onClose,
  onConferma,
}: {
  articolo: MagArticolo | null;
  motivo: string;
  setMotivo: (v: string) => void;
  pending: boolean;
  onClose: () => void;
  onConferma: () => void;
}) {
  return (
    <Dialog open={!!articolo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageX className="size-4 text-muted-foreground" />
            Non è prodotto finito
          </DialogTitle>
          <DialogDescription>
            <b>{articolo?.descrizione ?? articolo?.codice}</b> smette di bloccare
            l&apos;evadibilità delle commesse. Imballaggi, certificati e materiali di
            servizio non entrano nel conteggio.
          </DialogDescription>
        </DialogHeader>
        <input
          type="text"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Motivo (facoltativo): imballaggio, documento, materiale di consumo…"
          className="h-9 w-full rounded-md border bg-card px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <p className="text-[11px] text-muted-foreground">
          <Warehouse className="mr-1 inline size-3" />
          Si può disfare: l&apos;articolo resta in «Esclusi» e da lì torna nel conteggio.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Annulla
          </Button>
          <Button size="sm" disabled={pending} onClick={onConferma}>
            Escludi dal conteggio
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
